import { useRef, useCallback, useEffect } from "react";

/**
 * Smart polling hook with auto-cleanup
 *
 * Features:
 * - Configurable initial interval (e.g., 5s)
 * - Auto-stops when success condition met or timeout reached
 * - Cleans up on stop() or unmount
 * - Suitable for post-activation status checks
 *
 * @param {Object} config
 * @param {string} config.resourceId - Unique ID for the resource (required)
 * @param {Function} config.fetchFn - () => Promise<data> (required)
 * @param {Function} config.onSuccess - (data, metadata) when fetch succeeds
 * @param {Function} config.onError - (error) on fetch error
 * @param {Function} config.successCondition - (data) => true to stop polling
 * @param {number} config.initialDelay - Poll interval ms (default: 5000)
 * @param {number} config.maxDuration - Max time to poll ms (default: 60000)
 * @param {boolean} config.enabled - Whether polling is enabled
 * @param {boolean} config.autoStart - Start immediately (default: true)
 * @param {Function} config.onStop - Called when polling stops (success or timeout)
 *
 * @returns {Object} { stop, start, lastResult }
 */
export function useSmartPolling({
  resourceId,
  fetchFn,
  onSuccess = null,
  onError = null,
  successCondition = null,
  onStop = null,
  initialDelay = 5000,
  maxDuration = 60000,
  enabled = true,
  autoStart = true,
}) {
  const isPollingRef = useRef(false);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const attemptsRef = useRef(0);
  const lastResultRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
    startTimeRef.current = null;
    attemptsRef.current = 0;
  }, []);

  const startPolling = useCallback(() => {
    if (!enabled || !resourceId || !fetchFn) return;
    if (isPollingRef.current) return;

    isPollingRef.current = true;
    startTimeRef.current = Date.now();
    attemptsRef.current = 0;

    const executeFetch = async () => {
      try {
        attemptsRef.current += 1;
        const data = await fetchFn();
        lastResultRef.current = data;

        if (onSuccess) {
          onSuccess(data, {
            attempt: attemptsRef.current,
            elapsed: Date.now() - (startTimeRef.current || Date.now()),
          });
        }

        if (successCondition && successCondition(data)) {
          if (onStop) onStop();
          stopPolling();
          return;
        }
      } catch (error) {
        if (onError) onError(error);
      }

      const elapsed = Date.now() - (startTimeRef.current || Date.now());
      if (elapsed >= maxDuration) {
        if (onStop) onStop();
        stopPolling();
      }
    };

    executeFetch();
    intervalRef.current = setInterval(executeFetch, initialDelay);
  }, [
    enabled,
    resourceId,
    fetchFn,
    onSuccess,
    onError,
    successCondition,
    onStop,
    initialDelay,
    maxDuration,
    stopPolling,
  ]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    stop: stopPolling,
    start: startPolling,
    lastResult: lastResultRef.current,
  };
}

export default useSmartPolling;
